'use client';

import * as React from 'react';
import { useFloatingTree } from '@floating-ui/react';
import { useMenuRootContext } from "../root/MenuRootContext.js";
import { useBaseUiId } from "../../utils/useBaseUiId.js";
import { useComponentRenderer } from "../../utils/useComponentRenderer.js";
import { useForkRef } from "../../utils/useForkRef.js";
import { triggerOpenStateMapping } from "../../utils/popupStateMapping.js";
import { useCompositeListItem } from "../../composite/list/useCompositeListItem.js";
import { mergeProps } from "../../merge-props/index.js";
import { useMenuItem } from "../item/useMenuItem.js";

/**
 * A menu item that opens a submenu.
 * Renders a `<div>` element.
 *
 * Documentation: [Base UI Menu](https://base-ui.com/react/components/menu)
 */
export const MenuSubmenuTrigger = /*#__PURE__*/React.forwardRef(function SubmenuTriggerComponent(props, forwardedRef) {
  const {
    render,
    className,
    label,
    id: idProp,
    ...other
  } = props;
  const id = useBaseUiId(idProp);
  const {
    triggerProps: rootTriggerProps,
    parent,
    setTriggerElement,
    open,
    typingRef,
    disabled,
    allowMouseUpTriggerRef
  } = useMenuRootContext();
  if (parent.type !== 'menu') {
    throw new Error('Base UI: SubmenuTrigger must be placed in a nested Menu.');
  }
  const parentMenuContext = parent.context;
  const {
    activeIndex,
    itemProps,
    setActiveIndex
  } = parentMenuContext;
  const item = useCompositeListItem();
  const highlighted = activeIndex === item.index;
  const mergedRef = useForkRef(forwardedRef, item.ref);
  const {
    events: menuEvents
  } = useFloatingTree();
  const {
    getItemProps,
    rootRef: menuItemRef
  } = useMenuItem({
    closeOnClick: false,
    disabled,
    highlighted,
    id,
    menuEvents,
    ref: mergedRef,
    allowMouseUpTriggerRef,
    typingRef
  });
  const triggerRef = React.useRef(null);
  const menuTriggerRef = useForkRef(triggerRef, menuItemRef, setTriggerElement);
  const getTriggerProps = React.useCallback(externalProps => {
    return {
      ...getItemProps(externalProps),
      tabIndex: open || highlighted ? 0 : -1,
      ref: menuTriggerRef,
      onBlur() {
        if (highlighted) {
          setActiveIndex(null);
        }
      }
    };
  }, [getItemProps, highlighted, menuTriggerRef, open, setActiveIndex]);
  const state = React.useMemo(() => ({
    disabled,
    highlighted,
    open
  }), [disabled, highlighted, open]);
  const {
    renderElement
  } = useComponentRenderer({
    render: render || 'div',
    className,
    state,
    propGetter: externalProps => mergeProps(rootTriggerProps, itemProps, externalProps, getTriggerProps),
    customStyleHookMapping: triggerOpenStateMapping,
    extraProps: other
  });
  return renderElement();
});
if (process.env.NODE_ENV !== "production") MenuSubmenuTrigger.displayName = "MenuSubmenuTrigger";